import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Search, Code, PenTool, Database, Calendar, Users, MessageSquare, FileText, Share2, ArrowRight } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
const AGENTS = [{
  id: 'email',
  label: 'Email',
  icon: Mail,
  desc: 'Drafts and replies to threads'
}, {
  id: 'research',
  label: 'Research',
  icon: Search,
  desc: 'Deep dives and summaries'
}, {
  id: 'coding',
  label: 'Coding',
  icon: Code,
  desc: 'Writes and reviews code'
}, {
  id: 'design',
  label: 'Design',
  icon: PenTool,
  desc: 'Generates assets and layouts'
}, {
  id: 'data',
  label: 'Data',
  icon: Database,
  desc: 'Queries and analyzes metrics'
}, {
  id: 'calendar',
  label: 'Scheduling',
  icon: Calendar,
  desc: 'Manages your calendar'
}, {
  id: 'crm',
  label: 'CRM',
  icon: Users,
  desc: 'Updates Salesforce/HubSpot'
}, {
  id: 'support',
  label: 'Support',
  icon: MessageSquare,
  desc: 'Answers customer tickets'
}, {
  id: 'docs',
  label: 'Docs',
  icon: FileText,
  desc: 'Writes documentation'
}, {
  id: 'social',
  label: 'Social',
  icon: Share2,
  desc: 'Manages social channels'
}];
export function Agents() {
  return <div className="min-h-screen bg-[#1A1410] pt-32 pb-24 flex flex-col items-center justify-center relative overflow-hidden" data-id="element-7169" data-mp="w5qb6eeyo5x7">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#EA580C]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-7170" data-mp="108mphsxgxfvc" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col items-center text-center" data-id="element-7171" data-mp="1stwcosgl6i79">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-7172" data-mp="1f1q69y38jpl0">
          <div className="w-16 h-16 mx-auto bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(234,88,12,0.15)]" data-id="element-7173" data-mp="26tp831325mlk">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-8 h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-7174" data-mp="2lgnziw1x0h0a" />
          </div>
          <h1 className="text-4xl md:text-6xl font-title font-medium text-white tracking-tight leading-[1.1] mb-6" data-id="element-7175" data-mp="27hwk96bbgpak">
            Meet your new team.
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto" data-id="element-7176" data-mp="22k4jdmsp1su8">
            Giga isn't just a chat box. It's a fleet of specialized agents
            working together to handle every part of your business.
          </p>
        </motion.div>

        {/* Search / Command Bar Mockup */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="w-full max-w-2xl mx-auto mb-16 md:mb-24" data-id="element-7177" data-mp="1ot9kn3bwss4d">
          <div className="flex items-center justify-between p-2 pl-6 bg-white/5 border border-white/10 rounded-full backdrop-blur-md" data-id="element-7178" data-mp="14pkb7i7h7yeq">
            <div className="flex items-center gap-3 text-white/40" data-id="element-7179" data-mp="19n3pgi46cb6o">
              <span className="w-2 h-2 rounded-full bg-[#EA580C] animate-pulse" data-id="element-7180" data-mp="1emyl0yyw9oy2" />
              <span className="text-sm" data-id="element-7181" data-mp="1jmkyn9imx77e">Ask Giga to do anything...</span>
            </div>
            <WaitlistCTA spinnerClassName="text-[#1A1410]" className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#1A1410] rounded-full text-sm font-semibold hover:bg-neutral-200 transition-colors" data-id="element-7182" data-mp="lyub219aippk">
              Get started <ArrowRight size={16} data-id="element-7183" data-mp="vwgsovhz4vd8" />
            </WaitlistCTA>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="w-full max-w-4xl mx-auto" data-id="element-7184" data-mp="10o8llcolrd6j">
          <p className="text-xs font-semibold tracking-[2px] uppercase text-white/40 mb-10" data-id="element-7185" data-mp="864nhse45cqc">
            Giga runs 10+ specialized agents 24/7
          </p>

          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-id="element-7186" data-mp="24xs7smyyppfy">
            {AGENTS.map((agent, i) => <motion.div key={agent.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4,
            delay: 0.3 + i * 0.05
          }} className="group relative flex flex-col items-center" data-id="element-7187" data-mp="2jm4v6vjzhzyu" data-mpi={i}>
                {/* Tooltip */}
                <div className="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 pointer-events-none z-20 w-max max-w-[200px]" data-id="element-7188" data-mp="2ol810i7sga7v">
                  <div className="bg-[#2B211A] border border-white/10 text-white text-xs p-3 rounded-xl shadow-xl flex flex-col gap-1 text-center" data-id="element-7189" data-mp="18784zic2d4pw">
                    <span className="font-semibold text-[#EA580C]" data-id="element-7190" data-mp="139gb4iruz8j2">
                      {agent.label} Agent
                    </span>
                    <span className="text-white/70" data-id="element-7191" data-mp="3exun10g53snq">{agent.desc}</span>
                  </div>
                  {/* Tooltip arrow */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-[#2B211A]" data-id="element-7192" data-mp="3568c757prvli" />
                </div>

                {/* Icon Box */}
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 group-hover:text-[#EA580C] group-hover:bg-white/10 group-hover:border-[#EA580C]/30 transition-all duration-300 shadow-lg cursor-pointer" data-id="element-7193" data-mp="2w5abowwhi67o">
                  <agent.icon strokeWidth={1.5} className="w-6 h-6 md:w-7 md:h-7" data-id="element-7194" data-mp="2r5nvqfp9ubv6" />
                </div>

                {/* Label */}
                <span className="mt-3 text-[11px] md:text-xs font-medium text-white/50 group-hover:text-white/90 transition-colors" data-id="element-7195" data-mp="kgj2t7ps5207">
                  {agent.label}
                </span>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}